/**
 * 移动端优惠码列表页
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  List,
  PullToRefresh,
  Empty,
  Skeleton,
  InfiniteScroll,
  Dialog,
  Tag,
  Button,
  Card,
  Form,
  Input,
  Selector,
  Space,
  DatePicker,
  Dropdown,
  Checkbox,
} from 'antd-mobile';
import { adminDiscountApi, adminProductApi } from '../../api/index';
import Toast from 'antd-mobile/es/components/toast';

export default function MobileDiscountListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [discounts, setDiscounts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]); // 选中的商品ID
  const [productDropdownVisible, setProductDropdownVisible] = useState(false);
  const [form] = Form.useForm();

  // 加载商品列表
  useEffect(() => {
    adminProductApi.list({ pageSize: 100 }).then((data) => {
      const productList = data?.items || data || [];
      setProducts(productList);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchDiscounts(1);
  }, []);

  const fetchDiscounts = async (pageNum, append = false) => {
    try {
      const params = { page: pageNum, pageSize: 10 };
      const data = await adminDiscountApi.list(params);
      const newDiscounts = data?.items || [];
      if (append) {
        setDiscounts((prev) => [...prev, ...newDiscounts]);
      } else {
        setDiscounts(newDiscounts);
      }
      setHasMore(newDiscounts.length >= 10);
      setPage(pageNum);
    } catch (err) {
      Toast.show('获取优惠码列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!hasMore) return;
    await fetchDiscounts(page + 1, true);
  };

  const refreshData = async () => {
    await fetchDiscounts(1, false);
  };

  const openForm = (discount) => {
    setEditingDiscount(discount);
    if (discount) {
      // 解析商品ID列表
      let productIds = [];
      if (discount.applyToProducts) {
        if (Array.isArray(discount.applyToProducts)) {
          productIds = discount.applyToProducts.map(id => parseInt(id));
        } else if (typeof discount.applyToProducts === 'string') {
          productIds = discount.applyToProducts.split(',').map(id => parseInt(id)).filter(id => id);
        }
      }
      setSelectedProductIds(productIds);

      form.setFieldsValue({
        code: discount.code,
        title: discount.title,
        type: [discount.type],
        value: discount.value,
        minPurchase: discount.minPurchase,
        usageLimit: discount.usageLimit,
        isAuto: [discount.isAuto],
        startsAt: discount.startsAt?.slice(0, 16),
        endsAt: discount.endsAt?.slice(0, 16),
        enabled: [discount.enabled],
        minQuantity: discount.minQuantity,
      });
    } else {
      form.resetFields();
      setSelectedProductIds([]);
      form.setFieldsValue({
        type: ['percentage'],
        isAuto: [false],
        enabled: [true],
      });
    }
    setFormModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      // 从 Selector 数组中提取单个值
      const typeValue = Array.isArray(values.type) ? values.type[0] : values.type;
      const isAutoValue = Array.isArray(values.isAuto) ? values.isAuto[0] : values.isAuto;
      const enabledValue = Array.isArray(values.enabled) ? values.enabled[0] : values.enabled;

      const payload = {
        ...values,
        type: typeValue,
        isAuto: isAutoValue,
        enabled: enabledValue,
        value: typeValue === 'percentage' ? Math.round(values.value) : values.value,
        // 使用 selectedProductIds
        applyToProducts: selectedProductIds.length > 0
          ? selectedProductIds.join(',')
          : null,
        minQuantity: values.minQuantity || null,
      };
      if (editingDiscount) {
        await adminDiscountApi.update(editingDiscount.id, payload);
        Toast.show('更新成功');
      } else {
        await adminDiscountApi.create(payload);
        Toast.show('创建成功');
      }
      setFormModalVisible(false);
      fetchDiscounts(1);
    } catch (err) {
      Toast.show(err?.response?.data?.message || '操作失败');
    }
  };

  // 商品选择切换
  const toggleProduct = (productId) => {
    setSelectedProductIds(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // 商品全选/取消全选
  const toggleAllProducts = () => {
    if (selectedProductIds.length === products.length) {
      setSelectedProductIds([]);
    } else {
      setSelectedProductIds(products.map(p => p.id));
    }
  };

  const handleDelete = (id) => {
    Dialog.confirm({
      content: '确定要删除此优惠码吗？',
      onConfirm: async () => {
        try {
          await adminDiscountApi.delete(id);
          Toast.show('删除成功');
          fetchDiscounts(1);
        } catch (err) {
          Toast.show(err?.response?.data?.message || '删除失败');
        }
      },
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>优惠码管理</h2>
        <Button size="small" color="primary" onClick={() => openForm(null)}>
          新建
        </Button>
      </div>

      <PullToRefresh onRefresh={refreshData}>
        {discounts.length > 0 ? (
          <List>
            {discounts.map((discount) => (
              <List.Item
                key={discount.id}
                style={{
                  background: '#fff',
                  marginBottom: 12,
                  padding: '12px 16px',
                }}
                description={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    <Tag size="small" color={discount.type === 'percentage' ? 'primary' : 'success'}>
                      {discount.type === 'percentage' ? '百分比' : discount.type === 'fixed_amount' ? '固定金额' : '免运费'}
                    </Tag>
                    {discount.isAuto && <Tag size="small" color="warning">自动应用</Tag>}
                    {discount.applyToProducts && (
                      <Tag size="small" color="blue">
                        {discount.applyToProducts.split(',').length}个商品
                      </Tag>
                    )}
                    {discount.minQuantity && (
                      <Tag size="small" color="purple">
                        满{discount.minQuantity}件
                      </Tag>
                    )}
                    <Tag size="small" color={discount.enabled ? 'success' : 'default'}>
                      {discount.enabled ? '已启用' : '已禁用'}
                    </Tag>
                  </div>
                }
                extra={
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>
                      {discount.type === 'percentage' ? `${discount.value}%` : `$${discount.value}`}
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                      <span
                        style={{ fontSize: 13, color: '#1677ff', cursor: 'pointer' }}
                        onClick={() => openForm(discount)}
                      >
                        编辑
                      </span>
                      <span
                        style={{ fontSize: 13, color: '#ff4d4f', cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(discount.id);
                        }}
                      >
                        删除
                      </span>
                    </div>
                  </div>
                }
              >
                <div style={{ fontSize: 15, fontWeight: 500 }}>{discount.code || discount.title}</div>
                <div style={{ fontSize: 13, color: '#999' }}>{discount.title}</div>
              </List.Item>
            ))}
          </List>
        ) : (
          <div style={{ padding: '80px 0' }}>
            <Empty description="暂无优惠码" />
          </div>
        )}
      </PullToRefresh>

      {discounts.length > 0 && hasMore && (
        <InfiniteScroll loadMore={loadMore} style={{ paddingBottom: 60 }} />
      )}

      {/* 新建/编辑弹窗 */}
      <Dialog
        visible={formModalVisible}
        style={{ '--content-width': '90%' }}
        content={
          <div style={{ padding: '16px 0' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
              {editingDiscount ? '编辑优惠码' : '新建优惠码'}
            </h3>
            <Form
              form={form}
              layout="vertical"
              footer={
                <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                  <Button block type="submit" color="primary" onClick={() => form.submit()}>
                    保存
                  </Button>
                  <Button block variant="outline" onClick={() => setFormModalVisible(false)}>
                    取消
                  </Button>
                </div>
              }
              onFinish={handleSubmit}
            >
              <Form.Item name="code" label="优惠码">
                <Input placeholder="留空则为标题码" />
              </Form.Item>
              <Form.Item name="title" label="标题" rules={[{ required: true }]}>
                <Input placeholder="请输入标题" />
              </Form.Item>
              <Form.Item name="type" label="类型" initialValue="percentage" rules={[{ required: true }]}>
                <Selector
                  options={[
                    { label: '百分比折扣', value: 'percentage' },
                    { label: '固定金额', value: 'fixed_amount' },
                    { label: '免运费', value: 'free_shipping' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="value" label="折扣值">
                <Input placeholder="百分比或金额" type="number" />
              </Form.Item>
              <Form.Item name="minPurchase" label="最低消费">
                <Input placeholder="最低消费金额" type="number" />
              </Form.Item>
              <Form.Item name="usageLimit" label="使用次数限制">
                <Input placeholder="留空则无限制" type="number" />
              </Form.Item>

              {/* 适用商品 - 下拉多选 */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 14, marginBottom: 4, color: '#333' }}>适用商品</div>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>不限商品则留空</div>

                {/* 已选商品显示 */}
                <div
                  style={{
                    border: '1px solid #e5e5e5',
                    borderRadius: 4,
                    padding: 12,
                    background: '#fafafa',
                  }}
                  onClick={() => setProductDropdownVisible(!productDropdownVisible)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: selectedProductIds.length > 0 ? '#333' : '#999' }}>
                      {selectedProductIds.length > 0
                        ? `已选 ${selectedProductIds.length} 个商品`
                        : '点击选择商品'}
                    </span>
                    <span style={{ color: '#1677ff', fontSize: 12 }}>
                      {productDropdownVisible ? '收起' : '展开'}
                    </span>
                  </div>

                  {/* 已选商品标签 */}
                  {selectedProductIds.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                      {selectedProductIds.map(id => {
                        const product = products.find(p => p.id === id);
                        return (
                          <Tag
                            key={id}
                            color="primary"
                            style={{ borderRadius: 4 }}
                            onClose={() => toggleProduct(id)}
                            closable
                          >
                            {product?.title || `商品${id}`}
                          </Tag>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* 商品选择列表 */}
                {productDropdownVisible && (
                  <div
                    style={{
                      border: '1px solid #e5e5e5',
                      borderRadius: 4,
                      marginTop: 8,
                      maxHeight: 200,
                      overflowY: 'auto',
                      background: '#fff',
                    }}
                  >
                    {/* 全选按钮 */}
                    <div
                      style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        background: '#f5f5f5',
                      }}
                      onClick={toggleAllProducts}
                    >
                      <Checkbox
                        checked={selectedProductIds.length === products.length && products.length > 0}
                      />
                      <span style={{ fontSize: 14, fontWeight: 500 }}>全选 ({products.length} 个商品)</span>
                    </div>

                    {/* 商品列表 */}
                    {products.map(product => (
                      <div
                        key={product.id}
                        style={{
                          padding: '10px 12px',
                          borderBottom: '1px solid #f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                        onClick={() => toggleProduct(product.id)}
                      >
                        <Checkbox checked={selectedProductIds.includes(product.id)} />
                        <span style={{ fontSize: 14 }}>{product.title}</span>
                      </div>
                    ))}

                    {products.length === 0 && (
                      <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
                        暂无商品
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Form.Item
                name="minQuantity"
                label="最低商品数量"
                extra="不限数量则留空"
              >
                <Input placeholder="件数" type="number" />
              </Form.Item>
              <Form.Item name="isAuto" label="自动应用" initialValue={false}>
                <Selector
                  options={[
                    { label: '是', value: true },
                    { label: '否', value: false },
                  ]}
                />
              </Form.Item>
              <Form.Item name="enabled" label="状态" initialValue={true}>
                <Selector
                  options={[
                    { label: '启用', value: true },
                    { label: '禁用', value: false },
                  ]}
                />
              </Form.Item>
              <Form.Item name="startsAt" label="开始时间">
                <DatePicker precision="minute" />
              </Form.Item>
              <Form.Item name="endsAt" label="结束时间">
                <DatePicker precision="minute" />
              </Form.Item>
            </Form>
          </div>
        }
        closeOnMaskClick={false}
      />
    </div>
  );
}
